import React, { useState } from "react";
import ArrowCircleIcon from "assets/icons/ArrowCircleIcon";
import XIcon from "assets/icons/XIcon";
import styles from "./style.module.scss";

interface BugReporterModalProps {
  onClose: () => void;
  onSubmit: (data: { title: string; description?: string }) => void;
}

const BugReporterModal = ({ onClose, onSubmit }: BugReporterModalProps) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const [formErrors, setFormErrors] = useState({
    title: "",
  });

  const [fetchError, setFetchError] = useState(false);

  // Add event listener for Escape key
  React.useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // Add event listener when component mounts
    window.addEventListener("keydown", handleEscKey);

    // Remove event listener when component unmounts
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  const showDynamicValue = (key: "title") => {
    if (!!formErrors[key]) return formErrors[key];
    return formData[key];
  };

  const clearErrorMessage = (key: "title") => {
    if (!!formErrors[key]) setFormErrors({ ...formErrors, [key]: "" });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const validateForm = () => {
    const errors: { name?: string } = {};
    if (!formData.title.trim()) {
      errors.name = "This field can't be empty.";
    }
    return errors;
  };

  const handleFormSubmission = async () => {
    setFetchError(false);
    setFormErrors({ title: "" });

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors as { title: string });
    } else {
      try {
        await onSubmit(formData);
      } catch (error) {
        setFetchError(true);
      }
    }
  };

  return (
    <div className={styles.requestDemoModalWrapper}>
      <div className={styles.requestDemoModalContainer}>
        <div className={styles.requestDemoModalHeader}>
          <div className={styles.titleContainer}>
            <div className={styles.titleIcon}>
              <ArrowCircleIcon />
            </div>
            <div className={styles.titleText}>Report a Bug</div>
          </div>
          <div className={styles.closeButton} onClick={onClose}>
            <XIcon />
          </div>
        </div>

        <div className={styles.requestDemoModalBody}>
          <form className={styles.bodyForm}>
            <div className={styles.formColumn}>
              <input
                type="text"
                required
                id="title"
                name="title"
                placeholder="Title *"
                value={showDynamicValue("title")}
                onClick={() => clearErrorMessage("title")}
                onChange={handleInputChange}
                className={!!formErrors.title ? styles.errorInput : ""}
                onPaste={(e) => e.clipboardData.getData("text")}
              />
            </div>
            <div className={styles.formColumn}>
              <textarea
                id="description"
                name="description"
                placeholder="Description"
                value={formData.description}
                onChange={handleInputChange}
                className={styles.expandedTextarea}
                onPaste={(e) => e.clipboardData.getData("text")}
              />
            </div>
          </form>
        </div>

        <div className={styles.requestDemoModalFooter}>
          {fetchError && (
            <div className={styles.fetchErrorText}>Something went wrong.</div>
          )}
          <div className={styles.cancelButton} onClick={onClose}>
            Cancel
          </div>
          <div className={styles.submitButton} onClick={handleFormSubmission}>
            Submit
          </div>
        </div>
      </div>
    </div>
  );
};

export default BugReporterModal;
